import Phaser from 'phaser';
import type { InboxGame, InboxResponse } from '../../api/inbox-types.js';
import type { PublicGame } from '../../api/public-games-types.js';
import { apiBaseUrl } from '../../platform/index.js';
import { getAuth } from '../../store/auth.js';
import { cancelInvite } from '../../store/invites.js';
import {
  getOpenGames,
  onOpenGamesChange,
  refreshOpenGamesIfStale,
  type OpenGamesState,
} from '../../store/openGames.js';
import { navigateTo } from '../sceneNav.js';
import { attachBottomNav, bottomNavTopY, preloadNavAssets } from '../view/AppNav.js';
import {
  drawInboxGameCard,
  inboxGameBucketRank,
} from '../view/InboxGameCard.js';
import { mountPageHeader } from '../view/PageHeader.js';
import { drawPublicGameCard } from '../view/PublicGameCard.js';
import { SectionTabs } from '../view/SectionTabs.js';
import { COLORS, FONT_MONO } from '../style.js';
import { DESIGN_HEIGHT, DESIGN_WIDTH } from '../PhaserGame.js';

// "My Games" inbox. Lists every game the signed-in user is seated
// in, with YOUR-TURN games surfaced first.
//
// MP-Lobby-2.0 commit A.4: live API. The scene fetches /api/games
// on mount, walks a small state machine (loading → ready/error/
// signed-out), and renders the result list. Card taps navigate to
// the game via window.history.pushState + scene.start('Join').
//
// Card visuals + sorting helpers live in view/InboxGameCard.ts —
// same component HomeScene uses to render its inline preview.

const PAGE_BG_INT = 0x040713;

const SIDE_PADDING = 36;

const LINE_GAP_LOOSE = 18;
const SECTION_GAP = 28;

// Text scale matches the other terminal scenes for body-type rhythm.
const TEXT_SCALE = 1.5;
function fs(base: number): string {
  return `${Math.round(base * TEXT_SCALE)}px`;
}

// --- Card geometry ---
const CARD_WIDTH = DESIGN_WIDTH - SIDE_PADDING * 2;
const CARD_GAP = 14;

/** Tabs the inbox renders. All three render inline content now —
 *  ACTIVE / PAST come from the inbox API (games you're seated in),
 *  LOBBY comes from the openGames store (public lobbies anyone can
 *  join). Same card shape across all three. */
type InboxTabId = 'active' | 'past' | 'lobby';

type InboxLoadState =
  | { kind: 'loading' }
  | { kind: 'signed-out' }
  | { kind: 'ready'; games: readonly InboxGame[] }
  | { kind: 'error'; message: string };

interface InboxSceneInit {
  /** Which tab to open on. Defaults to 'active'. */
  readonly initialTab?: InboxTabId;
}

export class InboxScene extends Phaser.Scene {
  private content!: Phaser.GameObjects.Container;
  private contentHeight = 0;
  private contentTop = 0;
  private dragStart: { pointerY: number; contentY: number } | null = null;

  /** Active / Past / Lobby tab. Drives the content rebuild on
   *  tab change. */
  private activeTab: InboxTabId = 'active';

  /** Inbox load state — drives the active/past content. */
  private loadState: InboxLoadState = { kind: 'loading' };

  /** Open-games store snapshot — drives the lobby tab. Snapshot
   *  syncs via onOpenGamesChange so the scene re-renders on any
   *  cache mutation. */
  private openGamesState: OpenGamesState = getOpenGames();
  private openGamesUnsubscribe: (() => void) | null = null;

  /** Signed-in user's auth.users.id. Empty when signed out. Used to
   *  drive YOUR TURN comparisons + the "(you)" roster tag. */
  private meUserId: string = '';

  constructor() {
    super('Inbox');
  }

  preload(): void {
    preloadNavAssets(this);
    this.load.image('inboxLogo', '/assets/logo2.png');
  }

  init(data?: InboxSceneInit): void {
    this.contentHeight = 0;
    this.dragStart = null;
    this.activeTab = data?.initialTab ?? 'active';
    this.loadState = { kind: 'loading' };
    this.openGamesState = getOpenGames();
    this.openGamesUnsubscribe = null;
    this.meUserId = '';
  }

  create(): void {
    this.drawBackground();
    const { contentTop } = mountPageHeader({
      scene: this,
      logoAssetKey: 'inboxLogo',
      showBack: true,
    });
    this.contentTop = contentTop;
    this.content = this.add.container(0, contentTop);
    this.buildContent();
    this.applyScrollMask();
    this.installScrollInput();
    attachBottomNav(this, 'games');

    // Subscribe to open-games cache changes so the LOBBY tab re-
    // renders whenever the cache flips between loading / loaded /
    // error states.
    this.openGamesUnsubscribe = onOpenGamesChange((s) => {
      this.openGamesState = s;
      if (this.activeTab === 'lobby') this.rebuildContent();
    });
    // Trigger a stale-check refresh so users entering the LOBBY tab
    // after a long pause see fresh data without manual action. No-op
    // when cache is still fresh (30s TTL).
    refreshOpenGamesIfStale();
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.openGamesUnsubscribe?.();
      this.openGamesUnsubscribe = null;
    });

    // Kick the inbox fetch — render flips from 'loading' to 'ready'
    // / 'error' / 'signed-out' once the request resolves. We don't
    // await here: the scene paints the loading state immediately
    // and the fetch lands when the network does.
    void this.loadGames();
  }

  // --- Background ---

  private drawBackground(): void {
    const g = this.add.graphics();
    g.fillStyle(PAGE_BG_INT, 1);
    g.fillRect(0, 0, DESIGN_WIDTH, DESIGN_HEIGHT);
  }

  // --- Fetch ---

  private async loadGames(): Promise<void> {
    const auth = getAuth();
    if (!auth.session || !auth.user) {
      this.loadState = { kind: 'signed-out' };
      this.rebuildContent();
      return;
    }
    this.meUserId = auth.user.id;

    try {
      const res = await fetch(`${apiBaseUrl()}/api/games`, {
        headers: { Authorization: `Bearer ${auth.session.access_token}` },
      });
      if (!res.ok) {
        this.loadState = {
          kind: 'error',
          message: `Couldn't load games (HTTP ${res.status}).`,
        };
        this.rebuildContent();
        return;
      }
      const body = (await res.json()) as InboxResponse;
      this.loadState = { kind: 'ready', games: body.games };
      this.rebuildContent();
    } catch (err) {
      const detail = err instanceof Error ? err.message : String(err);
      this.loadState = {
        kind: 'error',
        message: `Network error: ${detail}`,
      };
      this.rebuildContent();
    }
  }

  // --- Tab + content management ---

  private handleTabSelect(id: InboxTabId): void {
    if (id === this.activeTab) return;
    this.activeTab = id;
    this.rebuildContent();
  }

  private rebuildContent(): void {
    this.content.removeAll(true);
    this.content.y = this.contentTop;
    this.buildContent();
  }

  private buildContent(): void {
    let y = 0;

    // Section nav butts directly against the logo (the logo lives in
    // scene root via mountPageHeader; content starts flush below it).
    y = this.addSectionNav(y);
    y += SECTION_GAP;

    switch (this.loadState.kind) {
      case 'loading':
        y = this.addCenteredMessage(y, 'Loading your games…');
        break;
      case 'signed-out':
        y = this.addCenteredMessage(
          y,
          'Sign in to see your games.',
          'You can sign in from the home screen.'
        );
        break;
      case 'error':
        y = this.addCenteredMessage(y, this.loadState.message, 'Try again later.');
        break;
      case 'ready':
        y = this.addReadyContent(y, this.loadState.games);
        break;
    }

    y += SECTION_GAP;
    y = this.addSignOff(y);

    this.contentHeight = y;
  }

  private addSectionNav(y: number): number {
    const games =
      this.loadState.kind === 'ready' ? this.loadState.games : [];
    const activeCount = games.filter((g) => g.status !== 'finished').length;
    const pastCount = games.filter((g) => g.status === 'finished').length;
    // Lobby count = number of public open games visible to the user.
    // Only meaningful when the openGames cache is loaded; falls back
    // to undefined (no badge) when loading / error / unloaded so the
    // tab label doesn't flicker numbers as the cache transitions.
    const lobbyCount =
      this.openGamesState.kind === 'loaded'
        ? this.openGamesState.list.games.length
        : undefined;
    const lobbyItem =
      lobbyCount !== undefined
        ? { id: 'lobby', label: 'LOBBY', count: lobbyCount }
        : { id: 'lobby', label: 'LOBBY' };
    const tabs = new SectionTabs(this, {
      x: 0,
      y,
      width: DESIGN_WIDTH,
      items: [
        { id: 'active', label: 'ACTIVE', count: activeCount },
        { id: 'past', label: 'PAST', count: pastCount },
        lobbyItem,
      ],
      activeIndex:
        this.activeTab === 'active'
          ? 0
          : this.activeTab === 'past'
            ? 1
            : 2,
      onSelect: (id) => this.handleTabSelect(id as InboxTabId),
    });
    this.content.add(tabs.root);
    return y + tabs.height;
  }

  private addReadyContent(y: number, games: readonly InboxGame[]): number {
    if (this.activeTab === 'active') {
      const sortable = games.filter((g) => g.status !== 'finished');
      const sorted = [...sortable].sort((a, b) => {
        const aRank = inboxGameBucketRank(a);
        const bRank = inboxGameBucketRank(b);
        if (aRank !== bRank) return aRank - bRank;
        return b.lastActionAt.localeCompare(a.lastActionAt);
      });
      if (sorted.length === 0) {
        return this.addCenteredMessage(
          y,
          'No active games yet.',
          'Start one from the home screen.'
        );
      }
      for (const g of sorted) {
        const { height } = drawInboxGameCard({
          scene: this,
          parent: this.content,
          x: SIDE_PADDING,
          y,
          width: CARD_WIDTH,
          game: g,
          meUserId: this.meUserId,
          onTap: (game) => this.openGame(game.id),
          onDecline: (game) => void this.handleDecline(game),
        });
        y += height + CARD_GAP;
      }
      return y;
    }

    if (this.activeTab === 'past') {
      // 'past' tab — finished games, most recent first.
      const finished = [...games]
        .filter((g) => g.status === 'finished')
        .sort((a, b) => b.lastActionAt.localeCompare(a.lastActionAt));
      if (finished.length === 0) {
        return this.addCenteredMessage(y, 'No completed games yet.');
      }
      for (const g of finished) {
        const { height } = drawInboxGameCard({
          scene: this,
          parent: this.content,
          x: SIDE_PADDING,
          y,
          width: CARD_WIDTH,
          game: g,
          meUserId: this.meUserId,
          onTap: (game) => this.openGame(game.id),
        });
        y += height + CARD_GAP;
      }
      return y;
    }

    // 'lobby' tab — public open games to browse + join. Data flows
    // through the openGames store; render its state machine inline
    // so the LOBBY tab feels uniform with Active/Past (no scene
    // jump). Tap → JoinScene via openGame().
    return this.addLobbyContent(y);
  }

  private addLobbyContent(y: number): number {
    switch (this.openGamesState.kind) {
      case 'unloaded':
      case 'loading':
        return this.addCenteredMessage(y, 'Loading open games…');
      case 'error':
        return this.addCenteredMessage(
          y,
          "Couldn't load open games",
          this.openGamesState.error
        );
      case 'loaded': {
        const open = this.openGamesState.list.games;
        if (open.length === 0) {
          return this.addCenteredMessage(
            y,
            'No open games right now.',
            'Be the first — host a public game from Home.'
          );
        }
        for (const g of open) {
          const { height } = drawPublicGameCard({
            scene: this,
            parent: this.content,
            x: SIDE_PADDING,
            y,
            width: CARD_WIDTH,
            game: g,
            onTap: (game: PublicGame) => this.openGame(game.id),
          });
          y += height + CARD_GAP;
        }
        return y;
      }
    }
  }

  private addCenteredMessage(
    y: number,
    headline: string,
    detail?: string
  ): number {
    const cx = DESIGN_WIDTH / 2;
    const a = this.add
      .text(cx, y, `[ ${headline} ]`, {
        fontFamily: FONT_MONO,
        fontSize: fs(15),
        color: COLORS.textMuted,
      })
      .setOrigin(0.5, 0);
    this.content.add(a);
    y += a.height;
    if (detail) {
      y += LINE_GAP_LOOSE;
      const b = this.add
        .text(cx, y, detail, {
          fontFamily: FONT_MONO,
          fontSize: fs(13),
          color: COLORS.textMuted,
        })
        .setOrigin(0.5, 0);
      this.content.add(b);
      y += b.height;
    }
    return y;
  }

  private addSignOff(y: number): number {
    const cx = DESIGN_WIDTH / 2;
    const text = this.add
      .text(cx, y, '// END INBOX', {
        fontFamily: FONT_MONO,
        fontSize: fs(11),
        color: COLORS.textMuted,
      })
      .setLetterSpacing(2)
      .setOrigin(0.5, 0);
    this.content.add(text);
    return y + text.height;
  }

  private openGame(gameId: string): void {
    // Push the canonical /game/:id URL so a refresh / share / deep
    // link works from this point, then hand off to JoinScene via
    // navigateTo so JoinScene's Back button returns the user here.
    window.history.pushState({}, '', `/game/${gameId}`);
    navigateTo(this, 'Join', { gameId });
  }

  private async handleDecline(game: InboxGame): Promise<void> {
    // Decline drops the caller's held seat. invitedSeatIndex is set
    // by the inbox endpoint specifically for this purpose — server
    // resolves the held row from invited_user_id = caller.id.
    if (game.invitedSeatIndex == null) {
      console.warn('decline: invitedSeatIndex missing', game.id);
      return;
    }
    const result = await cancelInvite(game.id, game.invitedSeatIndex);
    // 404 = row already gone (e.g. double-tap, or host cancelled
    // first). Desired end state already true — silent success.
    if (!result.ok && result.status !== 404) {
      console.warn('decline failed', {
        gameId: game.id,
        seatIndex: game.invitedSeatIndex,
        error: result.error,
        status: result.status,
      });
      // Refresh anyway — the row state on the server is authoritative
      // and may have changed (race with auto-claim, etc.).
    }
    void this.loadGames();
  }

  // --- Scroll plumbing ---

  private applyScrollMask(): void {
    const maskTop = this.contentTop;
    const maskBottom = bottomNavTopY();
    const maskShape = this.add.graphics();
    maskShape.fillStyle(0xffffff, 1);
    maskShape.fillRect(0, maskTop, DESIGN_WIDTH, maskBottom - maskTop);
    maskShape.setVisible(false);
    const mask = maskShape.createGeometryMask();
    this.content.setMask(mask);
  }

  private installScrollInput(): void {
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (pointer.y < this.contentTop) return;
      if (pointer.y > bottomNavTopY()) return;
      this.dragStart = {
        pointerY: pointer.y,
        contentY: this.content.y,
      };
    });
    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (!this.dragStart) return;
      const dy = pointer.y - this.dragStart.pointerY;
      const next = this.dragStart.contentY + dy;
      const visibleHeight = bottomNavTopY() - this.contentTop;
      const minY = Math.min(
        this.contentTop,
        this.contentTop - (this.contentHeight - visibleHeight)
      );
      const maxY = this.contentTop;
      this.content.y = Phaser.Math.Clamp(next, minY, maxY);
    });
    this.input.on('pointerup', () => {
      this.dragStart = null;
    });
    this.input.on('pointerupoutside', () => {
      this.dragStart = null;
    });
  }
}
